import { LoadingOutlined } from "@ant-design/icons";
import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import React from "react";
import { ToastContainer } from "react-toastify";

import { msalInstance } from "@/config/msal-config";
import { queryClient } from "@/config/react-query";
import { UserProvider } from "@/contexts/UserContext";
import { useAuthSync } from "@/hooks/useAuthSync";
import { logOut } from "@/utils/auth";
import { ErrorBoundary } from "@/utils/error-boundary";

const InProgressComponent = () => {
  return (
    <div className="flex h-full items-center justify-center gap-x-1.5 p-[100px]">
      <LoadingOutlined />
      <span>Loading...</span>
    </div>
  );
};

const MsalErrorComponent = ({ error }: any) => {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-[100px]">
      <p>
        This is a protected page and the following error occurred during
        authentication: <strong>{error?.errorCode}</strong>
      </p>
      <button
        className="rounded bg-gray-200 p-2 hover:bg-gray-100 hover:font-medium"
        onClick={() => {
          logOut();
        }}
      >
        Go Back
      </button>
    </div>
  );
};

/**
 * Runs the cross-app auth sync check once authentication has resolved, so a
 * logout or an account switch in the sibling app doesn't leave this app running
 * as the previous account. Renders nothing. See utils/authSync.
 */
const AuthSync = () => {
  useAuthSync();
  return null;
};

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <React.Suspense fallback={<InProgressComponent />}>
      <ErrorBoundary>
        <ToastContainer closeOnClick={false} draggable={false} />
        <MsalProvider instance={msalInstance}>
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            loadingComponent={InProgressComponent}
            errorComponent={MsalErrorComponent}
          >
            <AuthSync />
            <QueryClientProvider client={queryClient}>
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: "#3b82f6",
                    colorBgLayout: "#f5f5f5",
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: '"Poppins", sans-serif'
                  }
                }}
              >
                <UserProvider>{children}</UserProvider>
              </ConfigProvider>
            </QueryClientProvider>
          </MsalAuthenticationTemplate>
        </MsalProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
